// src/components/PicklistControl.tsx - FIXED: useMemo dependency
import React, { useState, useCallback, useMemo } from 'react';
import { X, Check, Loader2 } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import type { PicklistFilter, ExistenceFilter } from '../types';

interface PicklistControlProps {
  fieldName: string;
  isLoading: boolean;
}

const PicklistControl: React.FC<PicklistControlProps> = ({ fieldName, isLoading }) => {
  const { state, updateFilter } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [existenceState, setExistenceState] = useState<'none' | 'has_data' | 'is_empty'>('none');

  const getCurrentPicklistFilter = useCallback((fieldName: string): PicklistFilter | null => {
    const currentValue = state.searchState.filters[fieldName];
    if (currentValue && typeof currentValue === 'object' && 'type' in currentValue && currentValue.type === 'picklist') {
      return currentValue as PicklistFilter;
    }
    return null;
  }, [state.searchState.filters]);

  const currentFilterValue = state.searchState.filters[fieldName];
  const isExistenceFilter = currentFilterValue && typeof currentFilterValue === 'object' && 'type' in currentFilterValue && currentFilterValue.type === 'existence';

  // Update existence state based on current filter
  React.useEffect(() => {
    if (isExistenceFilter) {
      const existenceFilter = currentFilterValue as ExistenceFilter;
      setExistenceState(existenceFilter.has_value ? 'has_data' : 'is_empty');
    } else {
      setExistenceState('none');
    }
  }, [isExistenceFilter, currentFilterValue]);

  const currentValues = useMemo(() => {
    if (isExistenceFilter) return [];
    
    const filterValue = state.searchState.filters[fieldName];
    const currentFilter = getCurrentPicklistFilter(fieldName);
    
    if (Array.isArray(filterValue)) {
      return filterValue as string[];
    }
    return currentFilter?.values || [];
  }, [state.searchState.filters, fieldName, getCurrentPicklistFilter, isExistenceFilter]); // FIXED: Removed currentValues

  const filteredOptions = useMemo(() => {
    if (!state.metadata?.field_statistics[fieldName]) {
      return [];
    }
    
    const stats = state.metadata.field_statistics[fieldName];
    const valueCounts = stats.value_counts || {};
    const searchTermLower = searchTerm.toLowerCase();
    
    // Get custom ordering from schema if available
    const customOrdering = state.schema?.ui_config.picklist_ordering?.[fieldName];
    
    return Object.entries(valueCounts)
      .map(([value, count]) => ({ 
        value, 
        count: typeof count === 'number' ? count : 0, 
        label: value 
      }))
      .filter(option => {
        const matchesSearch = option.label.toLowerCase().includes(searchTermLower);
        const hasCount = option.count > 0;
        return matchesSearch && hasCount;
      })
      .sort((a, b) => {
        // Use custom ordering if available
        if (customOrdering) {
          const aIndex = customOrdering.indexOf(a.value);
          const bIndex = customOrdering.indexOf(b.value);
          // If both values are in the custom ordering, sort by their position
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          // If only one is in custom ordering, prioritize it
          if (aIndex !== -1 && bIndex === -1) return -1;
          if (aIndex === -1 && bIndex !== -1) return 1;
        }
        // Fallback to count-based sorting for values not in custom ordering
        if (a.count !== b.count) return b.count - a.count;
        return a.label.localeCompare(b.label);
      })
      .slice(0, 100);
  }, [state.metadata, state.schema, fieldName, searchTerm]); // FIXED: Removed currentValues

  const toggleOption = (optionValue: string) => {
    const newValues = currentValues.includes(optionValue)
      ? currentValues.filter(v => v !== optionValue)
      : [...currentValues, optionValue];
    
    if (newValues.length === 0) {
      updateFilter(fieldName, null);
    } else {
      const newFilter: PicklistFilter = {
        type: 'picklist',
        values: newValues,
        operator: 'OR'
      };
      updateFilter(fieldName, newFilter);
    }
    
    if (!currentValues.includes(optionValue)) {
      setSearchTerm('');
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // ✅ FIXED: Existence toggle
  const handleExistenceToggle = useCallback((newState: 'has_data' | 'is_empty') => {
    const finalState = existenceState === newState ? 'none' : newState;
    setExistenceState(finalState);
    
    if (finalState !== 'none') {
      setSearchTerm('');
      const existenceFilter: ExistenceFilter = {
        type: 'existence',
        has_value: finalState === 'has_data'
      };
      updateFilter(fieldName, existenceFilter);
    } else {
      updateFilter(fieldName, null);
    }
  }, [existenceState, fieldName, updateFilter]);

  return (
    <div className="enhanced-picklist">
      {/* ✅ FIXED: Always show picklist controls with visual state indication */}
      <div className={`picklist-controls ${existenceState !== 'none' ? 'existence-mode' : ''}`}>
        <div className="picklist-search-container">
          <input 
            type="text" 
            placeholder="Search options..."
            className="picklist-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button 
              className="picklist-clear-search" 
              onClick={clearSearch}
              type="button"
            >
              <X size={14} />
            </button>
          )}
        </div>
        
        <div className="picklist-options">
          {filteredOptions.length === 0 && !isLoading ? (
            <div className="no-options-message">
              {searchTerm ? 'No matching options found' : 'No options available'}
            </div>
          ) : (
            filteredOptions.map(option => (
              <div
                key={option.value}
                className={`picklist-option ${currentValues.includes(option.value) ? 'selected' : ''}`}
                onClick={() => toggleOption(option.value)}
              >
                <div className="option-checkbox">
                  <input
                    type="checkbox"
                    checked={currentValues.includes(option.value)}
                    onChange={() => toggleOption(option.value)}
                    className="checkbox-input"
                  />
                  <div className="checkbox-visual">
                    {currentValues.includes(option.value) && <Check size={10} />}
                  </div>
                </div>
                
                <span className="option-label" title={option.label}>
                  {option.label}
                </span>
                
                <span className="option-count">
                  {isLoading ? (
                    <Loader2 size={10} className="spinning" />
                  ) : (
                    option.count.toLocaleString()
                  )}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Existence Toggle Buttons - Always at Bottom */}
      <div className="existence-toggle-buttons">
        <button
          type="button"
          className={`existence-toggle-btn ${existenceState === 'has_data' ? 'active' : ''}`}
          onClick={() => handleExistenceToggle('has_data')}
        >
          Has Data
        </button>
        <button
          type="button"
          className={`existence-toggle-btn ${existenceState === 'is_empty' ? 'active' : ''}`}
          onClick={() => handleExistenceToggle('is_empty')}
        >
          Is Empty
        </button>
      </div>
    </div>
  );
};

export default PicklistControl;